import axios from 'axios';

export type UploadErrorKind =
  | 'cancel'
  | 'offline'
  | 'network'
  | 'server'
  | 'expired'
  | 'not_found'
  | 'file_unreadable'
  | 'fatal';

export const STALL_MESSAGE = 'stall';

// No progress event for this long means the connection is dead: abort and retry.
export const STALL_TIMEOUT = 30000;

// Delay before each retry attempt (1-based). Exhausted when attempt > length.
// Worst case with every attempt stalling: 7 × 30 s + 69 s ≈ 4.5 min to the error screen.
export const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000, 30000];

// Upload helpers retry on their own; the Api interceptor must not report
// every failed attempt to Sentry.
export const apiOptions = { skipSentry: true } as any;

export function retryDelay(attempt: number): number | null {
  if (attempt < 1 || attempt > RETRY_DELAYS.length) {
    return null;
  }
  return RETRY_DELAYS[attempt - 1];
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

// Understands both raw axios errors (chunk PUTs) and the plain objects
// our Api interceptor rejects with ({ isNetworkError } / { serverError } / { code }).
export function classifyUploadError(e: any): UploadErrorKind {
  if (!e || typeof e !== 'object') {
    return 'fatal';
  }
  if (e.kind) {
    return e.kind;
  }
  if (e.message === STALL_MESSAGE) {
    return 'network';
  }
  if (axios.isCancel(e) || e.message === 'cancel') {
    return 'cancel';
  }
  if (e.name === 'NotReadableError') {
    return 'file_unreadable';
  }
  if (e.code === 'not_found') {
    return 'not_found';
  }
  // Api interceptor rejects 4xx with the server payload ({ code, message }).
  if (typeof e.code === 'string' && !e.isAxiosError && !e.response) {
    return 'fatal';
  }
  if (e.isNetworkError) {
    return isOffline() ? 'offline' : 'network';
  }
  if (e.serverError) {
    return 'server';
  }

  const status: number | undefined = e.response?.status;
  if (status === undefined || status === 0) {
    if (e.response) {
      return 'fatal';
    }
    return isOffline() ? 'offline' : 'network';
  }
  if (status === 403) {
    return 'expired';
  }
  if (status === 429 || status === 409 || status === 423 || status >= 500) {
    return 'server';
  }
  return 'fatal';
}

export function isRetryableKind(kind: UploadErrorKind) {
  return kind === 'offline' || kind === 'network' || kind === 'server';
}

export function uploadError(kind: UploadErrorKind, message?: string) {
  const err: any = new Error(message || kind);
  err.kind = kind;
  return err;
}

// Attaches the classification to the error as-is: the Api interceptor rejects
// plain objects whose code/params the callers still need.
export function withKind(e: any, kind: UploadErrorKind) {
  if (e && typeof e === 'object') {
    return Object.assign(e, { kind });
  }
  return Object.assign(new Error(String(e || kind)), { kind });
}

// Reads a tiny slice to make sure the File is still readable. On iOS Safari a File
// picked from <input> can become unreadable after the tab was suspended.
export function probeFileReadable(file: File): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve();
    reader.onerror = () => {
      const err: any = new Error('file is not readable');
      err.name = 'NotReadableError';
      reject(err);
    };
    reader.readAsArrayBuffer(file.slice(0, Math.min(1024, file.size)));
  });
}

// Wraps an upload request: if no progress event arrives for stallMs,
// aborts it via cancel() with STALL_MESSAGE so the caller retries.
export function withStallDetector<T>(
  start: (onProgress: (event: ProgressEvent) => void) => Promise<T>,
  cancel: (reason: string) => void,
  onProgress: (event: ProgressEvent) => void,
  stallMs: number,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const arm = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => cancel(STALL_MESSAGE), stallMs);
  };
  arm();
  return start((event) => {
    arm();
    onProgress(event);
  }).finally(() => {
    if (timer) clearTimeout(timer);
  });
}
